/**
 * Admin Quest RPC definitions
 *
 * Admin endpoints for quest management - requires admin role.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Quest } from "@fm/db/frontend";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized } from "../errors.ts";

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Input schema for creating a quest - pick only user-provided fields from model
 * @since 1.0.0
 */
export const QuestCreateInput = Quest.json.pipe(
  Schema.pick("title", "situation", "theme", "scenarios"),
);

/**
 * Input schema for updating a quest - optional fields plus required id
 * @since 1.0.0
 */
export const QuestUpdateInput = Quest.json.pipe(
  Schema.pick("title", "situation", "theme", "scenarios"),
  Schema.partial,
  Schema.extend(Schema.Struct({ id: Schema.String })),
);

/**
 * Input schema for creating a draft quest - just title
 * @since 1.0.0
 */
export const QuestCreateDraftInput = Schema.Struct({
  title: Schema.NullOr(Schema.String),
});

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Quest RPC group
 *
 * All endpoints require admin role via AdminMiddleware.
 *
 * @since 1.0.0
 */
export class AdminQuestRpcs extends RpcGroup.make(
  Rpc.make("admin.quest.createDraft", {
    payload: QuestCreateDraftInput,
    success: Quest.json,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.quest.create", {
    payload: QuestCreateInput,
    success: Quest.json,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.quest.findMany", {
    success: Schema.Array(Quest.json),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.quest.findById", {
    payload: Schema.Struct({ id: Schema.String }),
    success: Quest.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.quest.update", {
    payload: QuestUpdateInput,
    success: Quest.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.quest.delete", {
    payload: Schema.Struct({ id: Schema.String }),
    success: Schema.Struct({ success: Schema.Boolean }),
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
) {}
